import React, {useState, useEffect, useCallback, useRef} from 'react'
import {useNavigate, useLocation} from 'react-router-dom'
import {Ticket, AdminUser, CustomerProfile, TASK_TYPE_LABELS, TASK_TYPE_ICONS, PRIORITY_LABELS, DEPARTMENTS} from '../tickets/core/_models'
import {
  getTaskStats,
  getMyTasks,
  getAllTasks,
  getTaskFollowUps,
  getAdminUsers,
  createTicketByAdmin,
  updateTicket,
  assignTicket,
  snoozeTask,
} from '../tickets/core/_requests'
import {getAdminToken} from '../auth/_requests'
import moment from 'moment'
import 'moment/locale/he'

const API_URL = process.env.REACT_APP_API_URL || ''

interface AccountSearchResult {
  _id: string
  first_name: string
  last_name: string
  email: string
  phone: string
}

type TabType = 'my' | 'followups' | 'all'

const TASK_TYPES = [
  {value: 'call_client', label: 'התקשר', icon: 'bi-telephone', subject: 'התקשר ללקוח'},
  {value: 'check_profiles', label: 'בדוק פרופילים', icon: 'bi-person-check', subject: 'בדוק פרופילים'},
  {value: 'fix_campaigns', label: 'תקן קמפיינים', icon: 'bi-gear', subject: 'תקן קמפיינים'},
  {value: 'onboarding', label: 'הטמעה', icon: 'bi-rocket-takeoff', subject: 'הטמעה'},
  {value: 'billing', label: 'חשבונות', icon: 'bi-credit-card', subject: 'טפל בחשבונות'},
  {value: 'follow_up', label: 'מעקב', icon: 'bi-arrow-repeat', subject: 'מעקב'},
  {value: 'general', label: 'כללי', icon: 'bi-pencil', subject: ''},
]

const FOLLOW_UP_PRESETS = [
  {label: 'היום', getValue: () => moment().endOf('day').toISOString()},
  {label: 'מחר', getValue: () => moment().add(1, 'day').startOf('day').hour(9).toISOString()},
  {label: 'עוד יומיים', getValue: () => moment().add(2, 'days').startOf('day').hour(9).toISOString()},
  {label: 'שבוע הבא', getValue: () => moment().add(1, 'week').startOf('day').hour(9).toISOString()},
]

const SNOOZE_PRESETS = [
  {label: '+2שע', preset: '2h'},
  {label: '+1י', preset: '1d'},
  {label: '+2י', preset: '2d'},
  {label: '+1ש', preset: '1w'},
]

const TasksBubble: React.FC = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showPanel, setShowPanel] = useState(false)
  const [activeTab, setActiveTab] = useState<TabType>('all')
  const [myTaskCount, setMyTaskCount] = useState(0)
  const [myTasks, setMyTasks] = useState<Ticket[]>([])
  const [allTasks, setAllTasks] = useState<Ticket[]>([])
  const [followUpTasks, setFollowUpTasks] = useState<Ticket[]>([])
  const [admins, setAdmins] = useState<AdminUser[]>([])
  const [loading, setLoading] = useState(false)

  // Create form state
  const [showCreate, setShowCreate] = useState(false)
  const [createSubject, setCreateSubject] = useState('')
  const [createTaskType, setCreateTaskType] = useState('')
  const [createPriority, setCreatePriority] = useState('medium')
  const [createDepartment, setCreateDepartment] = useState('')
  const [createAssignTo, setCreateAssignTo] = useState('')
  const [createFollowUp, setCreateFollowUp] = useState('')
  const [createAccountId, setCreateAccountId] = useState('')
  const [createAccountName, setCreateAccountName] = useState('')
  const [accountSearchQuery, setAccountSearchQuery] = useState('')
  const [accountResults, setAccountResults] = useState<AccountSearchResult[]>([])
  const [showAccountDropdown, setShowAccountDropdown] = useState(false)
  const [creating, setCreating] = useState(false)
  const [snoozing, setSnoozing] = useState<string | null>(null)
  const [showCustomFollowUp, setShowCustomFollowUp] = useState(false)
  const [showCustomSnooze, setShowCustomSnooze] = useState<string | null>(null)
  const [customSnoozeValue, setCustomSnoozeValue] = useState('')
  const [filterAccountId, setFilterAccountId] = useState<string | null>(null)
  const [urgency, setUrgency] = useState<'none' | 'pulse' | 'aggressive' | 'overdue'>('none')
  const [needsFlash, setNeedsFlash] = useState(false)
  const [overdueCount, setOverdueCount] = useState(0)

  const accountSearchRef = useRef<HTMLDivElement>(null)

  // Get current account: from admin URL (/admin/accounts/:id) or client JWT
  const getCurrentAccount = useCallback((): {id: string; name: string} | null => {
    // First check admin URL pattern
    const adminMatch = location.pathname.match(/\/admin\/accounts\/([a-f0-9]{24})/)
    if (adminMatch) return {id: adminMatch[1], name: ''} // name will be fetched

    // Decode client JWT from localStorage — works everywhere
    try {
      const lsValue = localStorage.getItem('kt-auth-react-v')
      if (lsValue) {
        const auth = JSON.parse(lsValue)
        if (auth?.api_token) {
          const payload = JSON.parse(atob(auth.api_token.split('.')[1]))
          if (payload?.user?._id) {
            return {id: payload.user._id, name: ''} // name fetched separately
          }
        }
      }
    } catch {}

    return null
  }, [location.pathname])

  // Fetch stats for badge
  const fetchStats = useCallback(async () => {
    try {
      const res = await getTaskStats()
      setMyTaskCount(res.data.myOpenTasks)
      setUrgency(res.data.urgency || 'none')
      setNeedsFlash(res.data.needsFlash || false)
      setOverdueCount(res.data.overdueFollowUps || 0)
    } catch {
      // silently fail
    }
  }, [])

  // Fetch tasks for active tab
  const fetchTasks = useCallback(async () => {
    if (!showPanel) return
    setLoading(true)
    try {
      if (activeTab === 'my') {
        const res = await getMyTasks()
        setMyTasks(res.data.tasks)
      } else if (activeTab === 'followups') {
        const res = await getTaskFollowUps()
        setFollowUpTasks(res.data.tasks)
      } else {
        const res = await getAllTasks(filterAccountId || undefined)
        setAllTasks(res.data.tasks)
      }
    } catch {
      // silently fail
    }
    setLoading(false)
  }, [showPanel, activeTab, filterAccountId])

  // Poll stats - faster when urgent
  useEffect(() => {
    fetchStats()
    const interval = setInterval(fetchStats, needsFlash ? 15000 : 60000)
    return () => clearInterval(interval)
  }, [fetchStats, needsFlash])

  // Browser notifications for overdue follow-ups
  useEffect(() => {
    if (overdueCount > 0 && 'Notification' in window) {
      if (Notification.permission === 'default') Notification.requestPermission()
      if (Notification.permission === 'granted') {
        new Notification('משימות מעקב באיחור', {
          body: `יש לך ${overdueCount} משימות מעקב שעבר זמנן`,
          tag: 'task-overdue',
          icon: '/favicon.ico',
        })
      }
    }
  }, [overdueCount])

  // Fetch tasks when panel opens or tab changes
  useEffect(() => {
    if (showPanel) {
      fetchTasks()
      if (admins.length === 0) {
        getAdminUsers().then((res) => setAdmins(res.data.admins)).catch(() => {})
      }
    }
  }, [showPanel, activeTab, fetchTasks])

  // Get current admin ID from JWT token
  const getCurrentAdminId = useCallback((): string | null => {
    try {
      const token = getAdminToken()
      if (!token) return null
      const payload = JSON.parse(atob(token.split('.')[1]))
      return payload?.adminId || null
    } catch {
      return null
    }
  }, [])

  // Auto-select admin assignee when admins list loads
  useEffect(() => {
    if (admins.length > 0 && !createAssignTo) {
      const adminId = getCurrentAdminId()
      if (adminId && admins.find((a) => a._id === adminId)) {
        setCreateAssignTo(adminId)
      }
    }
  }, [admins, createAssignTo, getCurrentAdminId])

  // Auto-select account when opening create form
  useEffect(() => {
    if (showCreate && !createAccountId) {
      const account = getCurrentAccount()
      if (account) {
        setCreateAccountId(account.id)
        setShowAccountDropdown(false)
        // Fetch account name from API
        const token = getAdminToken()
        const headers: HeadersInit = token ? {Authorization: `Bearer ${token}`} : {}
        fetch(`${API_URL}/admin/search-accounts?q=`, {credentials: 'include', headers})
          .then((r) => r.json())
          .then((data) => {
            const acc = data.accounts?.find((a: any) => a._id === account.id)
            if (acc) {
              const name = `${acc.first_name || ''} ${acc.last_name || ''}`.trim()
              const details = [acc.email, acc.phone].filter(Boolean).join(' | ')
              setCreateAccountName(details ? `${name} (${details})` : name)
            }
          })
          .catch(() => {})
      }
    }
  }, [showCreate, getCurrentAccount, createAccountId])

  // Search accounts — load all when dropdown opens, filter on type
  useEffect(() => {
    if (!showAccountDropdown) return
    const token = getAdminToken()
    const headers: HeadersInit = token ? {Authorization: `Bearer ${token}`} : {}
    const q = accountSearchQuery.length >= 1 ? accountSearchQuery : ''
    fetch(`${API_URL}/admin/search-accounts?q=${encodeURIComponent(q)}&days_grace=3`, {
      credentials: 'include',
      headers,
    })
      .then((r) => r.json())
      .then((data) => setAccountResults(data.accounts?.slice(0, 15) || []))
      .catch(() => {})
  }, [accountSearchQuery, showAccountDropdown])

  const getBubbleColor = () => {
    if (urgency === 'overdue') return '#f64e60'
    if (urgency === 'aggressive') return '#f64e60'
    if (urgency === 'pulse') return '#ffa800'
    if (myTaskCount > 7) return '#f64e60'
    if (myTaskCount > 3) return '#ffa800'
    return '#50cd89'
  }

  const handleComplete = async (taskId: string) => {
    try {
      await updateTicket(taskId, {status: 'resolved'})
      fetchStats()
      fetchTasks()
    } catch {
      // silently fail
    }
  }

  const handleSnooze = async (taskId: string, preset: string) => {
    setSnoozing(taskId)
    try {
      await snoozeTask(taskId, {preset})
      fetchTasks()
    } catch {
      // silently fail
    }
    setSnoozing(null)
  }

  const handleReassign = async (taskId: string, adminId: string) => {
    try {
      await assignTicket(taskId, adminId)
      fetchTasks()
    } catch {
      // silently fail
    }
  }

  const handleCreate = async () => {
    if (!createSubject.trim()) return
    setCreating(true)
    try {
      await createTicketByAdmin({
        subject: createSubject.trim(),
        is_internal: true,
        priority: createPriority as any,
        department: createDepartment || undefined,
        assigned_to: createAssignTo || undefined,
        account_id: createAccountId || undefined,
        task_type: createTaskType || undefined,
        follow_up_date: createFollowUp || undefined,
      })
      resetCreateForm()
      fetchStats()
      fetchTasks()
    } catch {
      // silently fail
    }
    setCreating(false)
  }

  const resetCreateForm = () => {
    setShowCreate(false)
    setCreateSubject('')
    setCreateTaskType('')
    setCreatePriority('medium')
    setCreateDepartment('')
    setCreateAssignTo(getCurrentAdminId() || '')
    setCreateFollowUp('')
    setShowCustomFollowUp(false)
    setCreateAccountId('')
    setCreateAccountName('')
    setAccountSearchQuery('')
    setShowAccountDropdown(false)
    setAccountResults([])
  }

  const selectTaskType = (type: typeof TASK_TYPES[0]) => {
    setCreateTaskType(type.value)
    if (type.subject) {
      const clientName = createAccountName || ''
      setCreateSubject(clientName ? `${type.subject} - ${clientName}` : type.subject)
    } else {
      setCreateSubject('')
    }
  }

  const getFollowUpStyle = (dateStr: string | null) => {
    if (!dateStr) return {}
    const date = moment(dateStr)
    const now = moment()
    if (date.isBefore(now)) return {color: '#f64e60', fontWeight: 700}
    if (date.isSame(now, 'day')) return {color: '#ffa800', fontWeight: 600}
    return {color: '#7e8299'}
  }

  const formatFollowUp = (dateStr: string | null) => {
    if (!dateStr) return ''
    const date = moment(dateStr)
    const now = moment()
    if (date.isBefore(now)) return `איחור ${date.fromNow(true)}`
    if (date.isSame(now, 'day')) return `היום ${date.format('HH:mm')}`
    if (date.isSame(now.clone().add(1, 'day'), 'day')) return `מחר ${date.format('HH:mm')}`
    return date.format('DD/MM HH:mm')
  }

  const getAccountName = (task: Ticket) => {
    const acc = task.account as CustomerProfile | null
    if (!acc || typeof acc === 'string') return ''
    return `${acc.first_name || ''} ${acc.last_name || ''}`.trim()
  }

  const currentTasks = activeTab === 'my' ? myTasks : activeTab === 'followups' ? followUpTasks : allTasks

  const flashAnimation = urgency === 'aggressive' || urgency === 'overdue'
    ? 'taskBubbleFlash 0.8s ease-in-out infinite'
    : urgency === 'pulse'
    ? 'taskBubblePulse 2s ease-in-out infinite'
    : 'none'

  return (
    <>
      <style>{`
        @keyframes taskBubbleFlash {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 16px rgba(0,0,0,0.2); }
          50% { transform: scale(1.15); box-shadow: 0 0 24px rgba(246, 78, 96, 0.6); }
        }
        @keyframes taskBubblePulse {
          0%, 100% { transform: scale(1); box-shadow: 0 4px 16px rgba(0,0,0,0.2); }
          50% { transform: scale(1.08); box-shadow: 0 0 20px rgba(255, 168, 0, 0.4); }
        }
      `}</style>
      {/* Tasks Bubble */}
      <div
        onClick={() => setShowPanel(!showPanel)}
        style={{
          position: 'fixed',
          bottom: '148px',
          left: '24px',
          width: '48px',
          height: '48px',
          borderRadius: '50%',
          background: getBubbleColor(),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
          zIndex: 1000,
          transition: 'background 0.3s ease',
          animation: flashAnimation,
        }}
      >
        <i className='bi bi-clipboard-check' style={{fontSize: '20px', color: '#fff'}}></i>
        {(myTaskCount > 0 || overdueCount > 0) && (
          <div
            style={{
              position: 'absolute',
              top: '-4px',
              right: '-4px',
              minWidth: '22px',
              height: '22px',
              borderRadius: '11px',
              background: overdueCount > 0 ? '#f64e60' : '#fff',
              color: overdueCount > 0 ? '#fff' : getBubbleColor(),
              fontSize: '11px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 4px',
              boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
            }}
          >
            {overdueCount > 0 ? overdueCount : myTaskCount}
          </div>
        )}
      </div>

      {/* Tasks Panel */}
      {showPanel && (
        <>
          <div
            style={{position: 'fixed', inset: 0, zIndex: 999}}
            onClick={() => {
              setShowPanel(false)
              setShowCreate(false)
            }}
          />
          <div
            style={{
              position: 'fixed',
              bottom: '148px',
              left: '80px',
              width: '420px',
              maxHeight: '580px',
              background: '#ffffff',
              borderRadius: '12px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              zIndex: 1001,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              direction: 'rtl',
            }}
          >
            {/* Panel Header */}
            <div
              style={{
                padding: '14px 20px',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <div style={{fontWeight: 600, fontSize: '16px', color: '#1e1e2d'}}>
                <i className='bi bi-clipboard-check' style={{marginLeft: '8px', color: getBubbleColor()}}></i>
                משימות פנימיות
              </div>
              <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
                <span
                  style={{
                    background: getBubbleColor(),
                    color: '#fff',
                    padding: '2px 10px',
                    borderRadius: '10px',
                    fontSize: '12px',
                    fontWeight: 600,
                  }}
                >
                  {myTaskCount} פתוחות
                </span>
                <div
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowCreate(!showCreate)
                  }}
                  style={{
                    padding: '4px 10px',
                    borderRadius: '20px',
                    background: showCreate ? '#2d80db' : 'transparent',
                    border: showCreate ? '1px solid #2d80db' : '1px solid #d1d5db',
                    color: showCreate ? '#fff' : '#5e6278',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: 500,
                    transition: 'all 0.2s ease',
                    whiteSpace: 'nowrap',
                  }}
                  onMouseEnter={(e) => {
                    if (!showCreate) {
                      e.currentTarget.style.background = 'rgba(54, 153, 255, 0.08)'
                      e.currentTarget.style.borderColor = '#3699ff'
                      e.currentTarget.style.color = '#3699ff'
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!showCreate) {
                      e.currentTarget.style.background = 'transparent'
                      e.currentTarget.style.borderColor = '#d1d5db'
                      e.currentTarget.style.color = '#5e6278'
                    }
                  }}
                >
                  <i className='bi bi-plus' style={{fontSize: '14px'}}></i>
                  חדש
                </div>
              </div>
            </div>

            {/* Create Form */}
            {showCreate && (
              <div
                style={{
                  padding: '16px 20px',
                  borderBottom: '2px solid rgba(54, 153, 255, 0.2)',
                  background: 'rgba(54, 153, 255, 0.03)',
                }}
              >
                {/* Task Type Shortcuts */}
                <div style={{display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '12px'}}>
                  {TASK_TYPES.map((type) => (
                    <button
                      key={type.value}
                      onClick={() => selectTaskType(type)}
                      style={{
                        padding: '4px 10px',
                        borderRadius: '6px',
                        border: createTaskType === type.value ? '1px solid #3699ff' : '1px solid #e4e6ef',
                        background: createTaskType === type.value ? 'rgba(54, 153, 255, 0.1)' : '#fff',
                        color: createTaskType === type.value ? '#3699ff' : '#5e6278',
                        fontSize: '12px',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.15s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                      }}
                    >
                      <i className={type.icon} style={{fontSize: '11px'}}></i>
                      {type.label}
                    </button>
                  ))}
                </div>

                {/* Account Search */}
                <div style={{marginBottom: '10px', position: 'relative'}} ref={accountSearchRef}>
                  {createAccountId && !showAccountDropdown ? (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        padding: '6px 10px',
                        borderRadius: '6px',
                        background: 'rgba(80, 205, 137, 0.1)',
                        border: '1px solid rgba(80, 205, 137, 0.3)',
                        fontSize: '13px',
                      }}
                    >
                      <span>
                        <i className='bi bi-person-fill' style={{marginLeft: '6px', color: '#50cd89'}}></i>
                        {createAccountName || createAccountId}
                      </span>
                      <button
                        onClick={() => {
                          setShowAccountDropdown(true)
                          setAccountSearchQuery('')
                        }}
                        style={{
                          padding: '2px 8px',
                          borderRadius: '4px',
                          border: '1px solid #e4e6ef',
                          background: '#fff',
                          color: '#7e8299',
                          fontSize: '11px',
                          cursor: 'pointer',
                        }}
                      >
                        <i className='bi bi-arrow-repeat' style={{marginLeft: '4px', fontSize: '10px'}}></i>
                        שנה
                      </button>
                    </div>
                  ) : (
                    <>
                      <div style={{position: 'relative'}}>
                        <input
                          type='text'
                          placeholder='חפש לקוח לפי שם, אימייל או טלפון...'
                          value={accountSearchQuery}
                          onChange={(e) => {
                            setAccountSearchQuery(e.target.value)
                            setShowAccountDropdown(true)
                          }}
                          onFocus={() => setShowAccountDropdown(true)}
                          style={{
                            width: '100%',
                            padding: '6px 10px',
                            paddingLeft: '28px',
                            borderRadius: '6px',
                            border: '1px solid #3699ff',
                            fontSize: '13px',
                            outline: 'none',
                            background: 'rgba(54, 153, 255, 0.03)',
                          }}
                        />
                        <i className='bi bi-search' style={{position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '12px', color: '#a1a5b7'}}></i>
                      </div>
                      {showAccountDropdown && accountResults.length > 0 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '100%',
                            right: 0,
                            left: 0,
                            background: '#fff',
                            border: '1px solid #e4e6ef',
                            borderRadius: '6px',
                            boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
                            zIndex: 10,
                            maxHeight: '200px',
                            overflowY: 'auto',
                          }}
                        >
                          {accountResults.map((acc) => (
                            <div
                              key={acc._id}
                              onClick={() => {
                                const name = `${acc.first_name || ''} ${acc.last_name || ''}`.trim()
                                const details = [acc.email, acc.phone].filter(Boolean).join(' | ')
                                setCreateAccountId(acc._id)
                                setCreateAccountName(details ? `${name} (${details})` : name)
                                setAccountSearchQuery('')
                                setShowAccountDropdown(false)
                                // Update subject if task type already selected
                                if (createTaskType) {
                                  const type = TASK_TYPES.find((t) => t.value === createTaskType)
                                  if (type?.subject) {
                                    setCreateSubject(`${type.subject} - ${name}`)
                                  }
                                }
                              }}
                              style={{
                                padding: '8px 12px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                borderBottom: '1px solid rgba(0,0,0,0.04)',
                                transition: 'background 0.1s',
                              }}
                              onMouseEnter={(e) => (e.currentTarget.style.background = '#f8f9fa')}
                              onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}
                            >
                              <div style={{fontWeight: 600}}>
                                {acc.first_name} {acc.last_name}
                              </div>
                              <div style={{color: '#a1a5b7', fontSize: '11px'}}>
                                {acc.email} | {acc.phone}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Subject */}
                <input
                  type='text'
                  placeholder='נושא המשימה...'
                  value={createSubject}
                  onChange={(e) => setCreateSubject(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) handleCreate()
                  }}
                  style={{
                    width: '100%',
                    padding: '6px 10px',
                    borderRadius: '6px',
                    border: '1px solid #e4e6ef',
                    fontSize: '13px',
                    marginBottom: '10px',
                    outline: 'none',
                  }}
                  autoFocus
                />

                {/* Row: Priority + Assign + Follow-up */}
                <div style={{display: 'flex', gap: '6px', marginBottom: '10px', flexWrap: 'wrap'}}>
                  <select
                    value={createPriority}
                    onChange={(e) => setCreatePriority(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      borderRadius: '6px',
                      border: '1px solid #e4e6ef',
                      fontSize: '12px',
                      minWidth: '80px',
                    }}
                  >
                    {Object.entries(PRIORITY_LABELS).map(([val, label]) => (
                      <option key={val} value={val}>
                        {label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={createDepartment}
                    onChange={(e) => {
                      setCreateDepartment(e.target.value)
                      setCreateAssignTo('') // reset assignee when department changes
                    }}
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      borderRadius: '6px',
                      border: '1px solid #e4e6ef',
                      fontSize: '12px',
                      minWidth: '80px',
                    }}
                  >
                    <option value=''>מחלקה...</option>
                    {DEPARTMENTS.map((d) => (
                      <option key={d.value} value={d.value}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                  <select
                    value={createAssignTo}
                    onChange={(e) => setCreateAssignTo(e.target.value)}
                    style={{
                      flex: 1,
                      padding: '5px 8px',
                      borderRadius: '6px',
                      border: '1px solid #e4e6ef',
                      fontSize: '12px',
                      minWidth: '80px',
                    }}
                  >
                    <option value=''>שייך ל...</option>
                    {(createDepartment ? admins.filter((a) => a.departments?.includes(createDepartment)) : admins).map((a) => (
                      <option key={a._id} value={a._id}>
                        {a.username}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Follow-up date shortcuts */}
                <div style={{display: 'flex', gap: '6px', marginBottom: showCustomFollowUp ? '4px' : '10px', alignItems: 'center', flexWrap: 'wrap'}}>
                  <span style={{fontSize: '12px', color: '#7e8299', whiteSpace: 'nowrap'}}>מעקב:</span>
                  {FOLLOW_UP_PRESETS.map((p) => (
                    <button
                      key={p.label}
                      onClick={() => {
                        setCreateFollowUp(p.getValue())
                        setShowCustomFollowUp(false)
                      }}
                      style={{
                        padding: '3px 8px',
                        borderRadius: '4px',
                        border: '1px solid #e4e6ef',
                        background:
                          createFollowUp && !showCustomFollowUp && moment(createFollowUp).isSame(moment(p.getValue()), 'day')
                            ? 'rgba(54, 153, 255, 0.1)'
                            : '#fff',
                        color: '#5e6278',
                        fontSize: '11px',
                        cursor: 'pointer',
                      }}
                    >
                      {p.label}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowCustomFollowUp(!showCustomFollowUp)}
                    style={{
                      padding: '3px 8px',
                      borderRadius: '4px',
                      border: showCustomFollowUp ? '1px solid #3699ff' : '1px solid #e4e6ef',
                      background: showCustomFollowUp ? 'rgba(54, 153, 255, 0.1)' : '#fff',
                      color: showCustomFollowUp ? '#3699ff' : '#5e6278',
                      fontSize: '11px',
                      cursor: 'pointer',
                    }}
                  >
                    <i className='bi bi-calendar3' style={{marginLeft: '3px', fontSize: '10px'}}></i>
                    מותאם
                  </button>
                  {createFollowUp && (
                    <i
                      className='bi bi-x'
                      style={{cursor: 'pointer', color: '#a1a5b7', fontSize: '14px'}}
                      onClick={() => {
                        setCreateFollowUp('')
                        setShowCustomFollowUp(false)
                      }}
                    ></i>
                  )}
                </div>
                {showCustomFollowUp && (
                  <div style={{marginBottom: '10px', display: 'flex', gap: '8px', alignItems: 'center'}}>
                    <div style={{flex: 1}}>
                      <label style={{fontSize: '11px', color: '#7e8299', display: 'block', marginBottom: '3px'}}>תאריך</label>
                      <input
                        type='date'
                        min={moment().format('YYYY-MM-DD')}
                        value={createFollowUp ? moment(createFollowUp).format('YYYY-MM-DD') : ''}
                        onChange={(e) => {
                          if (e.target.value) {
                            const existing = createFollowUp ? moment(createFollowUp) : moment().hour(9).minute(0)
                            const newDate = moment(e.target.value)
                              .hour(existing.hour())
                              .minute(existing.minute())
                            if (newDate.isBefore(moment())) {
                              newDate.hour(moment().hour() + 1).minute(0)
                            }
                            setCreateFollowUp(newDate.toISOString())
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 10px',
                          borderRadius: '6px',
                          border: '1px solid #e4e6ef',
                          fontSize: '13px',
                          outline: 'none',
                          direction: 'ltr',
                        }}
                      />
                    </div>
                    <div style={{width: '90px'}}>
                      <label style={{fontSize: '11px', color: '#7e8299', display: 'block', marginBottom: '3px'}}>שעה</label>
                      <select
                        value={createFollowUp ? moment(createFollowUp).format('HH:mm') : '09:00'}
                        onChange={(e) => {
                          const [h, m] = e.target.value.split(':').map(Number)
                          const existing = createFollowUp ? moment(createFollowUp) : moment()
                          const newDate = existing.clone().hour(h).minute(m)
                          if (newDate.isBefore(moment())) return
                          setCreateFollowUp(newDate.toISOString())
                        }}
                        style={{
                          width: '100%',
                          padding: '6px 8px',
                          borderRadius: '6px',
                          border: '1px solid #e4e6ef',
                          fontSize: '13px',
                          outline: 'none',
                          direction: 'ltr',
                        }}
                      >
                        {Array.from({length: 15}, (_, i) => i + 8).map((h) =>
                          [0, 30].map((m) => {
                            const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
                            const optDate = createFollowUp
                              ? moment(createFollowUp).clone().hour(h).minute(m)
                              : moment().hour(h).minute(m)
                            const isPast = optDate.isBefore(moment())
                            return (
                              <option key={val} value={val} disabled={isPast}>
                                {val}
                              </option>
                            )
                          })
                        )}
                      </select>
                    </div>
                  </div>
                )}

                {/* Submit */}
                <div style={{display: 'flex', gap: '8px'}}>
                  <button
                    onClick={handleCreate}
                    disabled={creating || !createSubject.trim()}
                    style={{
                      flex: 1,
                      padding: '7px 14px',
                      borderRadius: '6px',
                      border: 'none',
                      background: createSubject.trim() ? '#3699ff' : '#e4e6ef',
                      color: '#fff',
                      fontSize: '13px',
                      fontWeight: 600,
                      cursor: createSubject.trim() ? 'pointer' : 'not-allowed',
                      opacity: creating ? 0.6 : 1,
                    }}
                  >
                    {creating ? '...' : 'צור משימה'}
                  </button>
                  <button
                    onClick={resetCreateForm}
                    style={{
                      padding: '7px 14px',
                      borderRadius: '6px',
                      border: '1px solid #e4e6ef',
                      background: '#fff',
                      color: '#5e6278',
                      fontSize: '13px',
                      cursor: 'pointer',
                    }}
                  >
                    ביטול
                  </button>
                </div>
              </div>
            )}

            {/* Tabs */}
            <div
              style={{
                display: 'flex',
                borderBottom: '1px solid rgba(0,0,0,0.08)',
                padding: '0 20px',
              }}
            >
              {[
                {key: 'my' as TabType, label: 'המשימות שלי', badge: 0},
                {key: 'followups' as TabType, label: 'מעקבים', badge: overdueCount},
                {key: 'all' as TabType, label: 'כל המשימות', badge: 0},
              ].map((tab) => (
                <div
                  key={tab.key}
                  onClick={() => {
                    setActiveTab(tab.key)
                    setFilterAccountId(null)
                  }}
                  style={{
                    padding: '10px 16px',
                    fontSize: '13px',
                    fontWeight: activeTab === tab.key ? 600 : 400,
                    color: activeTab === tab.key ? '#3699ff' : tab.badge > 0 ? '#f64e60' : '#7e8299',
                    borderBottom: activeTab === tab.key ? '2px solid #3699ff' : '2px solid transparent',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  {tab.label}
                  {tab.badge > 0 && (
                    <span style={{
                      minWidth: '18px',
                      height: '18px',
                      borderRadius: '9px',
                      background: '#f64e60',
                      color: '#fff',
                      fontSize: '10px',
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '0 4px',
                    }}>
                      {tab.badge}
                    </span>
                  )}
                </div>
              ))}
            </div>

            {/* Tasks List */}
            <div style={{flex: 1, overflowY: 'auto', padding: '4px 0'}}>
              {loading ? (
                <div style={{padding: '32px', textAlign: 'center', color: '#a1a5b7'}}>
                  <div className='spinner-border spinner-border-sm' />
                </div>
              ) : currentTasks.length === 0 ? (
                <div style={{padding: '32px 20px', textAlign: 'center', color: '#a1a5b7'}}>
                  <i className='bi bi-check-circle' style={{fontSize: '32px', display: 'block', marginBottom: '8px'}}></i>
                  אין משימות פתוחות
                </div>
              ) : (
                currentTasks.map((task) => {
                  const accountName = getAccountName(task)
                  const assignedName = (task.assigned_to as AdminUser)?.username || ''
                  const taskIcon = task.task_type ? TASK_TYPE_ICONS[task.task_type] : 'bi-clipboard'
                  const isSnoozingThis = snoozing === task._id

                  return (
                    <div
                      key={task._id}
                      style={{
                        padding: '10px 20px',
                        borderBottom: '1px solid rgba(0,0,0,0.04)',
                        transition: 'background 0.1s',
                        position: 'relative',
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(0,0,0,0.02)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                    >
                      <div style={{display: 'flex', alignItems: 'flex-start', gap: '10px'}}>
                        {/* Complete checkbox */}
                        <div
                          onClick={() => handleComplete(task._id)}
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            border: '2px solid #c4c4c4',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0,
                            marginTop: '2px',
                            transition: 'all 0.15s ease',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = '#50cd89'
                            e.currentTarget.style.background = 'rgba(80, 205, 137, 0.1)'
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = '#c4c4c4'
                            e.currentTarget.style.background = 'transparent'
                          }}
                          title='סמן כהושלם'
                        >
                          <i className='bi bi-check' style={{fontSize: '14px', color: '#50cd89', opacity: 0.6}}></i>
                        </div>

                        {/* Task info */}
                        <div style={{flex: 1, minWidth: 0}}>
                          <div style={{display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '3px'}}>
                            <i className={taskIcon} style={{fontSize: '12px', color: '#7e8299'}}></i>
                            <span
                              style={{
                                fontSize: '13px',
                                fontWeight: 600,
                                color: '#1e1e2d',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                              onClick={() => {
                                setShowPanel(false)
                                navigate(`/admin/tickets/${task._id}`)
                              }}
                            >
                              {task.subject}
                            </span>
                          </div>

                          <div style={{display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap'}}>
                            {accountName && (
                              <span style={{fontSize: '11px', color: '#3699ff'}}>
                                <i className='bi bi-person' style={{marginLeft: '3px'}}></i>
                                {accountName}
                              </span>
                            )}
                            {assignedName && activeTab !== 'my' && (
                              <span style={{fontSize: '11px', color: '#7e8299'}}>
                                <i className='bi bi-person-badge' style={{marginLeft: '3px'}}></i>
                                {assignedName}
                              </span>
                            )}
                            {task.follow_up_date && (
                              <span style={{fontSize: '11px', ...getFollowUpStyle(task.follow_up_date)}}>
                                <i className='bi bi-clock' style={{marginLeft: '3px'}}></i>
                                {formatFollowUp(task.follow_up_date)}
                              </span>
                            )}
                            {task.priority === 'urgent' && (
                              <span
                                style={{
                                  fontSize: '10px',
                                  background: 'rgba(246, 78, 96, 0.1)',
                                  color: '#f64e60',
                                  padding: '1px 6px',
                                  borderRadius: '3px',
                                  fontWeight: 600,
                                }}
                              >
                                דחוף
                              </span>
                            )}
                            {task.priority === 'high' && (
                              <span
                                style={{
                                  fontSize: '10px',
                                  background: 'rgba(255, 168, 0, 0.1)',
                                  color: '#ffa800',
                                  padding: '1px 6px',
                                  borderRadius: '3px',
                                  fontWeight: 600,
                                }}
                              >
                                גבוה
                              </span>
                            )}
                          </div>

                          {/* Snooze + Reassign row (visible on hover via CSS would be better but inline works) */}
                          <div style={{display: 'flex', gap: '4px', marginTop: '6px', alignItems: 'center'}}>
                            {SNOOZE_PRESETS.map((s) => (
                              <button
                                key={s.preset}
                                onClick={() => handleSnooze(task._id, s.preset)}
                                disabled={isSnoozingThis}
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  border: '1px solid #e4e6ef',
                                  background: '#fff',
                                  color: '#7e8299',
                                  fontSize: '10px',
                                  cursor: 'pointer',
                                  opacity: isSnoozingThis ? 0.5 : 1,
                                }}
                              >
                                {s.label}
                              </button>
                            ))}
                            {showCustomSnooze === task._id ? (
                              <div style={{display: 'flex', gap: '3px', alignItems: 'center', flexWrap: 'wrap'}}>
                                <input
                                  type='date'
                                  min={moment().format('YYYY-MM-DD')}
                                  value={customSnoozeValue ? customSnoozeValue.split('T')[0] : ''}
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      const time = customSnoozeValue ? customSnoozeValue.split('T')[1] || '09:00' : '09:00'
                                      setCustomSnoozeValue(`${e.target.value}T${time}`)
                                    }
                                  }}
                                  style={{
                                    padding: '2px 4px',
                                    borderRadius: '3px',
                                    border: '1px solid #e4e6ef',
                                    fontSize: '10px',
                                    direction: 'ltr',
                                    width: '110px',
                                  }}
                                />
                                <select
                                  value={customSnoozeValue ? customSnoozeValue.split('T')[1] || '09:00' : '09:00'}
                                  onChange={(e) => {
                                    const date = customSnoozeValue ? customSnoozeValue.split('T')[0] : moment().format('YYYY-MM-DD')
                                    setCustomSnoozeValue(`${date}T${e.target.value}`)
                                  }}
                                  style={{
                                    padding: '2px 4px',
                                    borderRadius: '3px',
                                    border: '1px solid #e4e6ef',
                                    fontSize: '10px',
                                    direction: 'ltr',
                                    width: '65px',
                                  }}
                                >
                                  {Array.from({length: 15}, (_, i) => i + 8).map((h) =>
                                    [0, 30].map((m) => {
                                      const val = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
                                      const isToday = !!(customSnoozeValue && customSnoozeValue.split('T')[0] === moment().format('YYYY-MM-DD'))
                                      const isPast = isToday && moment().hour(h).minute(m).isBefore(moment())
                                      return (
                                        <option key={val} value={val} disabled={isPast}>
                                          {val}
                                        </option>
                                      )
                                    })
                                  )}
                                </select>
                                <button
                                  onClick={async () => {
                                    if (customSnoozeValue) {
                                      const snoozeDate = new Date(customSnoozeValue)
                                      if (snoozeDate <= new Date()) return
                                      setSnoozing(task._id)
                                      try {
                                        await snoozeTask(task._id, {custom_date: snoozeDate.toISOString()})
                                        fetchTasks()
                                      } catch {}
                                      setSnoozing(null)
                                      setShowCustomSnooze(null)
                                      setCustomSnoozeValue('')
                                    }
                                  }}
                                  disabled={!customSnoozeValue || new Date(customSnoozeValue) <= new Date()}
                                  style={{
                                    padding: '2px 6px',
                                    borderRadius: '3px',
                                    border: 'none',
                                    background: customSnoozeValue && new Date(customSnoozeValue) > new Date() ? '#3699ff' : '#e4e6ef',
                                    color: '#fff',
                                    fontSize: '10px',
                                    cursor: customSnoozeValue && new Date(customSnoozeValue) > new Date() ? 'pointer' : 'not-allowed',
                                  }}
                                >
                                  ✓
                                </button>
                                <i
                                  className='bi bi-x'
                                  style={{cursor: 'pointer', color: '#a1a5b7', fontSize: '12px'}}
                                  onClick={() => {
                                    setShowCustomSnooze(null)
                                    setCustomSnoozeValue('')
                                  }}
                                ></i>
                              </div>
                            ) : (
                              <button
                                onClick={() => {
                                  setShowCustomSnooze(task._id)
                                  const defaultDate = task.follow_up_date
                                    ? moment(task.follow_up_date)
                                    : moment().add(1, 'day').hour(9).minute(0)
                                  // If the default is in the past, use tomorrow 09:00
                                  const safeDate = defaultDate.isBefore(moment())
                                    ? moment().add(1, 'day').hour(9).minute(0)
                                    : defaultDate
                                  setCustomSnoozeValue(safeDate.format('YYYY-MM-DDTHH:mm'))
                                }}
                                style={{
                                  padding: '2px 6px',
                                  borderRadius: '3px',
                                  border: '1px solid #e4e6ef',
                                  background: '#fff',
                                  color: '#7e8299',
                                  fontSize: '10px',
                                  cursor: 'pointer',
                                }}
                              >
                                <i className='bi bi-calendar3' style={{fontSize: '9px'}}></i>
                              </button>
                            )}
                            <select
                              value={(task.assigned_to as AdminUser)?._id || ''}
                              onChange={(e) => handleReassign(task._id, e.target.value)}
                              style={{
                                padding: '2px 4px',
                                borderRadius: '3px',
                                border: '1px solid #e4e6ef',
                                fontSize: '10px',
                                color: '#7e8299',
                                marginRight: '4px',
                                maxWidth: '80px',
                              }}
                            >
                              <option value=''>העבר...</option>
                              {admins.map((a) => (
                                <option key={a._id} value={a._id}>
                                  {a.username}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>

            {/* Panel Footer */}
            <div
              onClick={() => {
                setShowPanel(false)
                navigate('/admin/tickets?is_internal=true&status=active')
              }}
              style={{
                padding: '12px 20px',
                borderTop: '1px solid rgba(0,0,0,0.08)',
                textAlign: 'center',
                cursor: 'pointer',
                color: '#3699ff',
                fontWeight: 500,
                fontSize: '13px',
                transition: 'background 0.15s ease',
              }}
              onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(54, 153, 255, 0.06)')}
              onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
            >
              צפה בכל המשימות
              <i className='bi bi-arrow-left' style={{marginRight: '6px'}}></i>
            </div>
          </div>
        </>
      )}
    </>
  )
}

export default TasksBubble
